import axios from 'axios';

// Production backend URL for mobile app - HARDCODED for reliability
const PRODUCTION_BACKEND_URL = 'https://api.ep-eg.com';

/**
 * Check if running in Capacitor mobile app
 */
const isCapacitorApp = () => {
  return typeof window !== 'undefined' && (
    window.Capacitor !== undefined ||
    window.location.protocol === 'capacitor:' ||
    window.location.protocol === 'ionic:' ||
    window.location.protocol === 'file:' ||
    (window.location.hostname === 'localhost' && window.navigator.userAgent.includes('wv'))
  );
};

/**
 * Get the backend base URL dynamically
 * For use across the application for image URLs, file downloads, etc.
 * @returns {string} Backend base URL
 */
export const getBackendBaseUrl = () => {
  // ALWAYS use production URL for Capacitor/mobile apps
  if (isCapacitorApp()) {
    return PRODUCTION_BACKEND_URL;
  }

  // Check for environment variable
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }

  // For web browser
  const hostname = window.location.hostname;

  // If localhost or empty, use production
  if (hostname === 'localhost' || hostname === '' || hostname === '127.0.0.1') {
    return PRODUCTION_BACKEND_URL;
  }

  // For development with local backend
  if (hostname.includes('192.168') || hostname.includes('10.0')) {
    return `http://${hostname}:8001`;
  }

  // Default to production
  return PRODUCTION_BACKEND_URL;
};

/**
 * Get API base URL (with /api suffix)
 */
const getApiBaseUrl = () => {
  return getBackendBaseUrl() + '/api';
};

// Create axios instance - baseURL will be set dynamically
const api = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
});

// Retry configuration
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;

/**
 * Delay helper for retry logic
 */
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Check if error is retryable (network errors, timeout, 5xx)
 */
const isRetryableError = (error) => {
  // Only retry safe/idempotent methods (GET, HEAD, OPTIONS)
  const safeMethod = ['get', 'head', 'options'].includes(
    error.config?.method?.toLowerCase()
  );
  if (!safeMethod) return false;
  // Network errors (no response)
  if (!error.response) return true;
  // Timeout errors
  if (error.code === 'ECONNABORTED') return true;
  // Server errors (5xx)
  if (error.response.status >= 500) return true;
  return false;
};

// Add request interceptor to set baseURL dynamically on EACH request
api.interceptors.request.use(
  (config) => {
    // Set baseURL dynamically
    config.baseURL = getApiBaseUrl();

    // Add auth token if available
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Initialize retry count
    config.__retryCount = config.__retryCount || 0;

    return config;
  },
  (error) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);

// Add response interceptor with retry logic
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const config = error.config;

    // If retryable and not exceeded max retries, retry the request
    if (config && isRetryableError(error) && config.__retryCount < MAX_RETRIES) {
      config.__retryCount += 1;
      const delayMs = RETRY_DELAY_MS * Math.pow(2, config.__retryCount - 1);

      await delay(delayMs);
      return api(config);
    }

    // Log detailed error info in development only
    if (process.env.NODE_ENV === 'development') {
      console.error('[API Error]', {
        url: config?.url,
        status: error.response?.status,
        message: error.message,
      });
    }

    // Handle 401 unauthorized
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Don't redirect on login page
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

export default api;